const inferredApiUrl =
  typeof window !== "undefined" && window.location.hostname !== "localhost"
    ? "https://api-ht.htzap.com.br"
    : "http://localhost:6101";

const tunnelApiUrl =
  typeof window !== "undefined" &&
  ["127.0.0.1", "localhost"].includes(window.location.hostname) &&
  window.location.port === "16100"
    ? `${window.location.protocol}//${window.location.hostname}:16101`
    : undefined;

const localApiUrl =
  typeof window !== "undefined" &&
  window.location.port === "6100"
    ? `${window.location.protocol}//${window.location.hostname}:6101`
    : undefined;

export const API_URL =
  tunnelApiUrl ||
  localApiUrl ||
  (import.meta.env.VITE_API_URL as string | undefined) ||
  inferredApiUrl;

export interface ApiFieldError {
  field?: string;
  code: string;
  message: string;
}

const statusMessages: Record<number, string> = {
  400: "Confira os dados informados.",
  401: "Sua sessão expirou. Entre novamente.",
  403: "Você não possui permissão para realizar esta ação.",
  404: "O recurso solicitado não foi encontrado.",
  409: "A operação conflita com dados já existentes.",
  422: "Corrija os campos destacados.",
  429: "Muitas tentativas. Aguarde alguns instantes e tente novamente.",
  500: "Não foi possível concluir a operação. Tente novamente em alguns instantes.",
  502: "Não foi possível concluir a operação. Tente novamente em alguns instantes.",
  503: "Não foi possível concluir a operação. Tente novamente em alguns instantes.",
  504: "A operação demorou mais que o esperado. Tente novamente."
};

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function text(value: unknown) {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

export function normalizeFieldErrors(value: unknown): ApiFieldError[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    const candidate = record(item);
    const message = text(candidate.message);
    if (!message) return [];
    return [{
      ...(text(candidate.field) ? { field: text(candidate.field) } : {}),
      code: text(candidate.code) || "INVALID_FIELD",
      message
    }];
  });
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly details?: unknown,
    readonly code = "REQUEST_FAILED",
    readonly errors: ApiFieldError[] = [],
    readonly requestId?: string
  ) {
    super(message);
    this.name = "ApiError";
  }

  get fieldErrors() {
    return Object.fromEntries(
      this.errors
        .filter((error): error is ApiFieldError & { field: string } => Boolean(error.field))
        .map((error) => [error.field, error.message])
    );
  }

  fieldError(field: string) {
    return this.errors.find((error) => error.field === field)?.message;
  }
}

export function userErrorMessage(error: unknown, fallback: string) {
  return error instanceof ApiError ? error.message : fallback;
}

export function isRetryableApiError(error: unknown) {
  return error instanceof ApiError &&
    (error.code === "NETWORK_ERROR" || error.code === "TIMEOUT" || error.status >= 500);
}

export function normalizeApiResponseError(
  status: number,
  body: unknown,
  requestIdHeader: string | null,
  path: string
) {
  const payload = record(body);
  const code = text(payload.code) || `HTTP_${status}`;
  const errors = normalizeFieldErrors(payload.errors || payload.issues);
  const requestId = text(payload.requestId) || requestIdHeader || undefined;
  const suppliedMessage = Array.isArray(payload.message)
    ? payload.message.filter((item) => typeof item === "string").join(" ")
    : text(payload.message) || text(payload.error);
  const trustedServerMessage = status < 500 || (Boolean(requestId) && Boolean(payload.code));
  const message = status === 401 && !path.includes("/v1/auth/login")
    ? statusMessages[401]!
    : trustedServerMessage && suppliedMessage
      ? suppliedMessage
      : statusMessages[status] || statusMessages[500]!;

  return new ApiError(message, status, body, code, errors, requestId);
}

export async function api<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const headers = new Headers(options.headers);
  if (options.body && !(options.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }
  const timeout = new AbortController();
  let timedOut = false;
  const timer = globalThis.setTimeout(() => {
    timedOut = true;
    timeout.abort();
  }, 30_000);
  const cancelFromCaller = () => timeout.abort(options.signal?.reason);
  options.signal?.addEventListener("abort", cancelFromCaller, { once: true });
  let response: Response;
  try {
    response = await fetch(`${API_URL}${path}`, {
      ...options,
      headers,
      signal: timeout.signal,
      credentials: "include"
    });
  } catch (cause) {
    if (timedOut) {
      throw new ApiError(statusMessages[504]!, 504, undefined, "TIMEOUT");
    }
    if (options.signal?.aborted) throw cause;
    throw new ApiError(
      "Não foi possível conectar ao servidor. Verifique sua conexão e tente novamente.",
      0,
      undefined,
      "NETWORK_ERROR"
    );
  } finally {
    globalThis.clearTimeout(timer);
    options.signal?.removeEventListener("abort", cancelFromCaller);
  }
  if (response.status === 204) return undefined as T;
  const contentType = response.headers.get("content-type") || "";
  const body = contentType.includes("application/json")
    ? await response.json()
    : await response.text();
  if (!response.ok) {
    const error = normalizeApiResponseError(
      response.status,
      body,
      response.headers.get("x-request-id"),
      path
    );
    if (response.status === 401 && !path.includes("/v1/auth/login")) {
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("htzap:session-expired"));
      }
    }
    throw error;
  }
  return body as T;
}

export function jsonBody(value: unknown): Pick<RequestInit, "body"> {
  return { body: JSON.stringify(value) };
}

export function mediaUrl(attachmentId: string): string {
  return `${API_URL}/v1/media/${attachmentId}`;
}

export function teamChatMediaUrl(attachmentId: string): string {
  return `${API_URL}/v1/team-chat/attachments/${attachmentId}`;
}

export function ticketNoteMediaUrl(attachmentId: string): string {
  return `${API_URL}/v1/ticket-note-attachments/${attachmentId}`;
}

export function quickReplyAttachmentUrl(attachmentId: string): string {
  return `${API_URL}/v1/quick-reply-attachments/${attachmentId}`;
}
